import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  DatePicker,
  Button,
  Statistic,
  Table,
  Space,
  Dropdown,
  Tag,
  Radio,
  Progress,
  Modal,
  Descriptions,
  Spin,
  message,
} from 'antd';
import {
  DownloadOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  SettingOutlined,
  RiseOutlined,
  FallOutlined,
  MessageOutlined,
  EyeOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import './Dashboard.css';
import dayjs from 'dayjs';
import { 
  StatisticsFilters,
  SummaryStats,
  MessageDetailStats,
  UserReadDetail,
  DailyTrendData,
  OrganizationStats,
  MessageTypeStats
} from '../services/statisticsService';
import { fastStatisticsService as statisticsService } from '../services/statisticsService.fast';
import { supabase } from '../services/supabase';
import { logger } from '../utils/logger';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false); // 중복 실행 방지용
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>([dayjs().subtract(6, 'days'), dayjs()]);
  const [messageType, setMessageType] = useState<'all' | 'NOTICE' | 'TBM'>('all');
  const [dateFilterType, setDateFilterType] = useState<'sent' | 'read'>('sent');
  const [selectedMessage, setSelectedMessage] = useState<MessageDetailStats | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100']
  });
  
  // 통계 데이터 상태
  const [summaryStats, setSummaryStats] = useState<SummaryStats>({ totalSent: 0, totalRead: 0, readRate: 0, readRateChange: 0 });
  const [dailyTrendData, setDailyTrendData] = useState<DailyTrendData[]>([]);
  const [messageDetailData, setMessageDetailData] = useState<MessageDetailStats[]>([]);
  const [organizationData, setOrganizationData] = useState<OrganizationStats[]>([]);
  const [messageTypeData, setMessageTypeData] = useState<MessageTypeStats[]>([]);
  const [userReadDetails, setUserReadDetails] = useState<UserReadDetail[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // 조직 목록
  const [companies, setCompanies] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string | undefined>(undefined);
  const [selectedBranch, setSelectedBranch] = useState<string | undefined>(undefined);

  // 조직 목록 로드
  useEffect(() => {
    loadOrganizations();
  }, []);
  
  // 통계 데이터 로드 - 필터 변경 시 즉시 실행
  useEffect(() => {
    if (dateRange?.[0] && dateRange?.[1]) {
      loadStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, dateFilterType, messageType, selectedCompany, selectedBranch]);

  // 조직 목록 로드
  const loadOrganizations = async () => {
    try {
      // 회사 목록 조회
      const { data: companiesData } = await supabase
        .from('companies')
        .select('*')
        .order('name');
      
      setCompanies(companiesData || []);
      
      // 지점 목록 조회
      const { data: branchesData } = await supabase
        .from('branches')
        .select('*, companies!inner(name)')
        .order('branch_name');
      
      setBranches(branchesData || []);
    } catch (error) {
      logger.error('Error loading organizations', error);
    }
  };
  
  // 통계 데이터 로드
  const loadStatistics = useCallback(async () => {
    // 중복 실행 방지
    if (loadingRef.current) {
      logger.debug('이미 로딩 중, 중복 실행 방지');
      return;
    }
    
    loadingRef.current = true;
    const startTime = Date.now();
    setLoading(true);
    
    try {
      const filters: StatisticsFilters = {
        dateRange,
        dateFilterType,
        messageType,
        companyId: selectedCompany,
        branchId: selectedBranch
      };
      
      logger.debug('통계 로딩 시작', { timestamp: new Date().toISOString() });
      
      // 병렬로 모든 통계 데이터 로드 (성능 최적화)
      const [summary, daily, messages, orgs, types] = await Promise.allSettled([
        statisticsService.getSummaryStats(filters),
        statisticsService.getDailyTrend(filters),
        statisticsService.getMessageDetailStats(filters),
        statisticsService.getOrganizationStats(filters),
        statisticsService.getMessageTypeStats(filters)
      ]);
      
      // 결과 처리
      if (summary.status === 'fulfilled') {
        setSummaryStats(summary.value);
      } else {
        logger.error('Error loading summary stats', summary.reason);
      }
      
      if (daily.status === 'fulfilled') {
        setDailyTrendData(daily.value);
      } else {
        logger.error('Error loading daily trend', daily.reason);
      }
      
      if (messages.status === 'fulfilled') {
        setMessageDetailData(messages.value);
      } else {
        logger.error('Error loading message details', messages.reason);
      }
      
      if (orgs.status === 'fulfilled') {
        setOrganizationData(orgs.value);
      } else {
        logger.error('Error loading organization stats', orgs.reason);
      }
      
      if (types.status === 'fulfilled') {
        setMessageTypeData(types.value);
      } else {
        logger.error('Error loading message type stats', types.reason);
      }
    } catch (error) {
      logger.error('Error loading statistics', error);
      message.error('통계 데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
      loadingRef.current = false;
      const endTime = Date.now();
      logger.debug('통계 로딩 완료', { duration: `${endTime - startTime}ms` });
    }
  }, [dateRange, dateFilterType, messageType, selectedCompany, selectedBranch]);

  // 날짜 범위 선택 프리셋
  const dateRangePresets: Array<{
    label: string;
    value: [dayjs.Dayjs, dayjs.Dayjs];
  }> = [
    { label: '오늘', value: [dayjs(), dayjs()] },
    { label: '어제', value: [dayjs().subtract(1, 'd'), dayjs().subtract(1, 'd')] },
    { label: '이번 주', value: [dayjs().startOf('week'), dayjs().endOf('week')] },
    { label: '지난 주', value: [dayjs().subtract(1, 'week').startOf('week'), dayjs().subtract(1, 'week').endOf('week')] },
    { label: '이번 달', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
    { label: '지난 달', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
    { label: '최근 7일', value: [dayjs().subtract(6, 'd'), dayjs()] },
    { label: '최근 30일', value: [dayjs().subtract(29, 'd'), dayjs()] },
  ];

  // 메시지 상세 조회
  const handleMessageDetail = async (record: MessageDetailStats) => {
    setSelectedMessage(record);
    setDetailModalVisible(true);
    setDetailLoading(true);
    
    try {
      const details = await statisticsService.getUserReadDetails(record.id);
      setUserReadDetails(details);
    } catch (error) {
      logger.error('Error loading user read details', error, { messageId: record.id });
      message.error('사용자 읽음 상세를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setDetailLoading(false);
    }
  };
  
  // 조회 버튼 클릭
  const handleSearch = () => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      message.warning('조회 기간을 선택해주세요.');
      return;
    }
    loadStatistics();
  };
  
  // 초기화 버튼 클릭
  const handleReset = () => {
    setDateRange([dayjs().subtract(6, 'days'), dayjs()]);
    setDateFilterType('sent');
    setMessageType('all');
    setSelectedCompany(undefined);
    setSelectedBranch(undefined);
  };

  const exportMenu = {
    items: [
      {
        key: 'pdf',
        icon: <FilePdfOutlined />,
        label: 'PDF로 다운로드',
      },
      {
        key: 'excel',
        icon: <FileExcelOutlined />,
        label: 'Excel로 다운로드',
      },
      {
        type: 'divider' as const,
      },
      {
        key: 'schedule',
        icon: <SettingOutlined />,
        label: '정기 보고서 설정',
      },
    ],
  };


  return (
    <div className="dashboard">
      <div className="page-header">
        <h1 className="page-title">대시보드</h1>
        <Dropdown menu={exportMenu} placement="bottomRight">
          <Button icon={<DownloadOutlined />}>
            보고서 생성
          </Button>
        </Dropdown>
      </div>

      {/* 상단 필터 컨트롤 */}
      <Card className="filter-card">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Space>
                <span>📅 조회 기간:</span>
                <Radio.Group value={dateFilterType} onChange={(e) => setDateFilterType(e.target.value)}>
                  <Radio value="sent">발송일 기준</Radio>
                  <Radio value="read">읽은 날짜 기준</Radio>
                </Radio.Group>
              </Space>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <RangePicker
                style={{ width: '100%' }}
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                presets={dateRangePresets}
                format="YYYY-MM-DD"
              />
            </Col>
            <Col span={4}>
              <Select
                value={messageType}
                onChange={setMessageType}
                style={{ width: '100%' }}
              >
                <Option value="all">전체 메시지</Option>
                <Option value="NOTICE">공지사항</Option>
                <Option value="TBM">TBM</Option>
              </Select>
            </Col>
            <Col span={3}>
              <Select
                value={selectedCompany || 'all'}
                onChange={(value) => {
                  setSelectedCompany(value === 'all' ? undefined : value);
                  setSelectedBranch(undefined);
                }}
                style={{ width: '100%' }}
                placeholder="회사 선택"
              >
                <Option value="all">전체 회사</Option>
                {companies.map(company => (
                  <Option key={company.id} value={company.id}>{company.name}</Option>
                ))}
              </Select>
            </Col>
            <Col span={3}>
              <Select
                value={selectedBranch || 'all'}
                onChange={(value) => setSelectedBranch(value === 'all' ? undefined : value)}
                style={{ width: '100%' }}
                placeholder="지점 선택"
                disabled={!selectedCompany}
              >
                <Option value="all">전체 지점</Option>
                {branches
                  .filter(branch => !selectedCompany || branch.company_id === selectedCompany)
                  .map(branch => (
                    <Option key={branch.id} value={branch.id}>{branch.branch_name}</Option>
                  ))}
              </Select>
            </Col>
            <Col span={6}>
              <Space>
                <Button type="primary" onClick={handleSearch} loading={loading}>조회</Button>
                <Button onClick={handleReset}>초기화</Button>
              </Space>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* KPI 카드 */}
      <Spin spinning={loading}>
        <Row gutter={[16, 16]} className="kpi-cards">
          <Col xs={24} sm={12} lg={8}>
            <Card>
              <Statistic
                title="전체 발송 수"
                value={summaryStats.totalSent}
                prefix={<MessageOutlined />}
                suffix="건"
              />
              <Progress percent={100} showInfo={false} strokeColor="#1890ff" />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card>
              <Statistic
                title="전체 읽음 수"
                value={summaryStats.totalRead}
                prefix={<EyeOutlined />}
                suffix="건"
                valueStyle={{ color: '#52c41a' }}
              />
              <Progress percent={summaryStats.readRate} strokeColor="#52c41a" />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card>
              <Statistic
                title="평균 읽음률"
                value={summaryStats.readRate}
                prefix={<CheckCircleOutlined />}
                suffix="%"
                valueStyle={{ color: '#1890ff' }}
              />
              <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                전주 대비 
                <span style={{ color: summaryStats.readRateChange >= 0 ? '#52c41a' : '#f5222d' }}>
                  {summaryStats.readRateChange >= 0 ? '+' : ''}{summaryStats.readRateChange}%
                </span> 
                {summaryStats.readRateChange >= 0 ? <RiseOutlined /> : <FallOutlined />}
              </div>
            </Card>
          </Col>
        </Row>
      </Spin>

      {/* 차트 섹션 */}
      <Row gutter={[16, 16]} className="chart-section">
        <Col xs={24}>
          <Card title="일별 발송/읽음 추이">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <RechartsTooltip />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="NOTICE" stroke="#1890ff" name="공지사항" strokeWidth={2} />
                <Line yAxisId="left" type="monotone" dataKey="TBM" stroke="#52c41a" name="TBM" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="readRate" stroke="#fa8c16" name="읽음률(%)" strokeWidth={2} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="메시지 유형별 통계">
            {(() => {
              const hasData = messageTypeData.some(item => item.sent > 0);
              const chartData = hasData ? messageTypeData : [{ name: '데이터 없음', value: 100, color: '#d9d9d9' }];
              
              return (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => hasData ? `${entry.name}: ${entry.value}%` : entry.name}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ marginTop: '16px' }}>
                    {hasData ? (
                      messageTypeData.map((item) => (
                        <div key={item.name} style={{ marginBottom: '8px' }}>
                          <Space>
                            <div style={{ width: '12px', height: '12px', backgroundColor: item.color, borderRadius: '2px' }} />
                            <span>{item.name}:</span>
                            <span>발송 {item.sent}건</span>
                            <span>읽음 {item.read}건</span>
                            <span>({item.sent > 0 ? ((item.read / item.sent) * 100).toFixed(1) : '0'}%)</span>
                          </Space>
                        </div>
                      ))
                    ) : (
                      <div style={{ textAlign: 'center', color: '#999' }}>
                        선택한 기간에 발송된 메시지가 없습니다.
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={
            !selectedCompany 
              ? "회사별 읽음률" 
              : selectedBranch 
                ? "지점 읽음률"
                : "지점별 읽음률"
          }>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={organizationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="readRate" fill="#1890ff" name="읽음률(%)">
                  {organizationData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.readRate >= 90 ? '#52c41a' : '#1890ff'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* 메시지별 상세 통계 테이블 */}
      <Card title="메시지별 상세 통계" className="detail-table-card">
        <Table
          columns={[
            {
              title: '유형',
              dataIndex: 'type',
              key: 'type',
              width: 80,
              render: (type: string) => (
                <Tag color={type === 'NOTICE' ? 'blue' : 'green'}>
                  {type === 'NOTICE' ? '공지사항' : 'TBM'}
                </Tag>
              ),
            },
            {
              title: '제목',
              dataIndex: 'title',
              key: 'title',
              ellipsis: true,
            },
            {
              title: '발송일시',
              dataIndex: 'sentAt',
              key: 'sentAt',
              width: 150,
            },
            {
              title: '대상',
              dataIndex: 'targetCount',
              key: 'targetCount',
              width: 80,
              align: 'center',
            },
            {
              title: '발송',
              dataIndex: 'sentCount',
              key: 'sentCount',
              width: 80,
              align: 'center',
            },
            {
              title: '읽음',
              dataIndex: 'readCount',
              key: 'readCount',
              width: 80,
              align: 'center',
            },
            {
              title: '읽음률',
              dataIndex: 'readRate',
              key: 'readRate',
              width: 100,
              align: 'center',
              render: (rate: number) => (
                <div>
                  <Progress percent={rate} size="small" status={rate >= 90 ? 'success' : 'normal'} />
                </div>
              ),
            },
            {
              title: '상세',
              key: 'action',
              width: 80,
              align: 'center',
              render: (_: any, record: any) => (
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handleMessageDetail(record)}
                >
                  보기
                </Button>
              ),
            },
          ]}
          dataSource={messageDetailData}
          pagination={tablePagination}
          onChange={(pagination) => {
            setTablePagination({
              ...tablePagination,
              current: pagination.current || 1,
              pageSize: pagination.pageSize || 10
            });
          }}
        />
      </Card>

      {/* 메시지 상세 모달 */}
      <Modal
        title="메시지 읽음 상세"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            닫기
          </Button>
        ]}
      >
        {selectedMessage && (
          <div>
            <Descriptions bordered column={2} style={{ marginBottom: '24px' }}>
              <Descriptions.Item label="제목" span={2}>
                {selectedMessage.title}
              </Descriptions.Item>
              <Descriptions.Item label="유형">
                <Tag color={selectedMessage.type === 'NOTICE' ? 'blue' : 'green'}>
                  {selectedMessage.type === 'NOTICE' ? '공지사항' : 'TBM'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="발송일시">
                {selectedMessage.sentAt}
              </Descriptions.Item>
              <Descriptions.Item label="전체 대상">
                {selectedMessage.targetCount}명
              </Descriptions.Item>
              <Descriptions.Item label="읽음">
                {selectedMessage.readCount}명 ({selectedMessage.readRate}%)
              </Descriptions.Item>
            </Descriptions>

            <Spin spinning={detailLoading}>
              <Table
                columns={[
                  { title: '이름', dataIndex: 'name', key: 'name', width: 100 },
                  { title: '전화번호', dataIndex: 'phone', key: 'phone', width: 130 },
                  { title: '회사', dataIndex: 'company', key: 'company', width: 120 },
                  { title: '지점', dataIndex: 'branch', key: 'branch', width: 120 },
                  {
                    title: '상태',
                    dataIndex: 'status',
                    key: 'status',
                    width: 80,
                    align: 'center',
                    render: (status: string) => (
                      <Tag color={status === 'read' ? 'green' : 'orange'}>
                        {status === 'read' ? '읽음' : '미읽음'}
                      </Tag>
                    ),
                  },
                  {
                    title: '읽은 시간',
                    dataIndex: 'readAt',
                    key: 'readAt',
                    width: 160,
                    render: (readAt: string | null) => readAt || '-',
                  },
                ]}
                dataSource={userReadDetails}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  showTotal: (total) => `전체 ${total}명`
                }}
                scroll={{ y: 400 }}
                size="small"
              />
            </Spin>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;